import { useCometControlBrowserSkillEnabledVariation } from '#pplx/frontend/apps/ask/generated/assignment-hooks/useCometControlBrowserSkillEnabledVariation.js';
import { useGateSearchModelSelection } from '#pplx/frontend/apps/ask/hooks/search-model-selection-hooks/useGateSearchModelSelection.js';
import { useIsModeDisabledByOrg } from '#pplx/frontend/apps/ask/hooks/search-model-selection-hooks/useIsModeDisabledByOrg.js';
import { useIsModeVisible } from '#pplx/frontend/apps/ask/hooks/search-model-selection-hooks/useIsModeVisible.js';
import { useSharedSelectSearchModel } from '#pplx/frontend/apps/ask/hooks/search-model-selection-hooks/useSharedSelectSearchModel.js';
import { useThreadConfig } from '#pplx/frontend/apps/ask/providers/ThreadSettingsProviderShared.js';
import { getSearchModeLabel } from '#pplx/frontend/apps/ask/search-modes/core/search-modes-names.js';
import { useSearchModeForSearchModel } from '#pplx/frontend/apps/ask/search/hooks/useSearchModeForSearchModel.js';
import { useSearchModels } from '#pplx/frontend/ask-models/hooks/useSearchModels.js';
import { getSearchModeIcon, isSidecarSupportedMode, SEARCH_MODES_DISPLAY, SearchMode, SearchModel, } from '#pplx/frontend/ask-models/types/search-modes.js';
import { useMemo } from 'react';
import { useIntl } from 'react-intl';
const EMPTY_OMITTED_SEARCH_MODES = [];
export const useAtMentionSearchModes = ({ enabled, separateComputerMode = false, omittedSearchModes = EMPTY_OMITTED_SEARCH_MODES, onSearchModeSelected, selectSearchModeOverride, openPaywallUpsell, }) => {
    const { $t } = useIntl();
    const selectSearchModel = useSharedSelectSearchModel({ openPaywallUpsell });
    const { getDefaultModelForMode } = useSearchModels({ reason: 'use_at_mention_search_modes' });
    const getSearchModeForSearchModel = useSearchModeForSearchModel();
    const { configuredModel } = useThreadConfig();
    const isModeVisible = useIsModeVisible();
    const isModeDisabledByOrg = useIsModeDisabledByOrg();
    const { variation: isControlBrowserSkillEnabled } = useCometControlBrowserSkillEnabledVariation({
        defaultValue: false,
    });
    const { gateSearchModeSelection } = useGateSearchModelSelection({ openPaywallUpsell });
    const mentionSearchModesOptions = useMemo(() => {
        if (!enabled) {
            return [];
        }
        const currentSearchMode = getSearchModeForSearchModel(configuredModel);
        const displayModes = SEARCH_MODES_DISPLAY.filter((mode) => {
            // the currently selected mode is excluded
            if (mode === currentSearchMode) {
                return false;
            }
            if (omittedSearchModes.includes(mode)) {
                return false;
            }
            if (!isModeVisible(mode)) {
                return false;
            }
            if (mode === SearchMode.BROWSER_AGENT && isControlBrowserSkillEnabled) {
                return false;
            }
            if (import.meta.env.VITE_IS_SIDECAR_BUILD && !isSidecarSupportedMode(mode)) {
                return false;
            }
            return true;
        });
        return displayModes.map((mode) => {
            const label = getSearchModeLabel(mode);
            const icon = getSearchModeIcon(mode);
            const useSeparatedComputerPresentation = separateComputerMode && mode === SearchMode.ASI;
            return {
                uuid: mode,
                variant: 'search-mode',
                label: useSeparatedComputerPresentation
                    ? $t({
                        defaultMessage: 'Switch to Computer',
                        description: 'Label for the Computer option in the slash command search mode menu when it is separated from other search modes.',
                        id: 'a2fe9ad8bf',
                    })
                    : $t(label),
                subtitle: useSeparatedComputerPresentation
                    ? $t({
                        defaultMessage: 'More powerful tools and skills',
                        description: 'Byline shown under the Computer option in the slash command search mode menu.',
                        id: '0b271adb83',
                    })
                    : undefined,
                icon: { type: 'icon', icon },
                separatorBefore: useSeparatedComputerPresentation,
                action: () => {
                    if (selectSearchModeOverride) {
                        if (isModeDisabledByOrg(mode))
                            return false;
                        return selectSearchModeOverride(mode);
                    }
                    const defaultModel = getDefaultModelForMode(mode);
                    const gated = gateSearchModeSelection(defaultModel);
                    if (gated) {
                        return false;
                    }
                    // FIXME: Part of dynamic model config migration - remove cast after updating callers
                    const applied = selectSearchModel(defaultModel);
                    if (applied) {
                        onSearchModeSelected?.(mode);
                    }
                    return applied;
                },
                convertToNode: false,
            };
        });
    }, [
        enabled,
        $t,
        selectSearchModel,
        getDefaultModelForMode,
        getSearchModeForSearchModel,
        configuredModel,
        omittedSearchModes,
        isModeVisible,
        isControlBrowserSkillEnabled,
        gateSearchModeSelection,
        separateComputerMode,
        onSearchModeSelected,
        selectSearchModeOverride,
        isModeDisabledByOrg,
    ]);
    return {
        isAtMentionSearchModesEnabled: enabled,
        mentionSearchModesOptions,
    };
};
//# sourceMappingURL=useAtMentionSearchModes.js.map